import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ArrowRight, CheckCircle2, Clock } from 'lucide-react';
import { t } from '@/i18n';
import { planSources } from '@/data/planSources';

const statusDot = { live: 'text-emerald-500', demo: 'text-blue-500', ready: 'text-amber-500' };
const effortStyle = {
  low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  high: 'bg-red-50 text-red-700 border-red-200',
};

const Row = ({ label, children }) => (
  <div className="text-xs">
    <div className="text-muted-foreground">{label}</div>
    <div>{children}</div>
  </div>
);

// Every way the loading plan can reach the app, with what it takes to connect it.
export const PlanSources = () => (
  <div className="space-y-4">
    <div className="text-sm text-muted-foreground">{t('sources.intro')}</div>
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {planSources.map(s => (
        <Card key={s.id} className={cn('p-4 space-y-3 flex flex-col', s.pilot && 'ring-1 ring-primary/30')} data-testid={`source-${s.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="text-sm font-semibold">{t(`sources.${s.id}.title`)}</div>
            {s.pilot && <Badge className="shrink-0 text-[10px]">{t('sources.pilot')}</Badge>}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="outline" className="text-[10px] gap-1 font-normal">
              <span className={statusDot[s.status]} aria-hidden="true">●</span> {t(`sources.status.${s.status}`)}
            </Badge>
            <Badge variant="outline" className={cn('text-[10px] font-normal', effortStyle[s.effort])}>
              {t('sources.effort')}: {t(`sources.effortLevel.${s.effort}`)}
            </Badge>
            <Badge variant="outline" className="text-[10px] font-normal">
              {s.needsIt ? t('sources.needsIt') : t('sources.noIt')}
            </Badge>
          </div>
          <Row label={t('sources.how')}>{t(`sources.${s.id}.how`)}</Row>
          <Row label={t('sources.fromOmya')}>{t(`sources.${s.id}.it`)}</Row>
          {s.lastRun && (
            <div className="flex items-center gap-1.5 rounded-lg bg-blue-50 px-2.5 py-1.5 text-xs text-blue-800">
              <Clock className="h-3 w-3" /> {t('sources.lastRun')} {s.lastRun.time}: {s.lastRun.loadings} {t('sources.loadings')}
              {s.lastRun.errors === 0 && <CheckCircle2 className="h-3 w-3 ml-auto" />}
            </div>
          )}
          {s.link && (
            <Button asChild size="sm" variant="outline" className="mt-auto gap-1">
              <Link to={s.link}>{t('sources.open')} <ArrowRight className="h-3 w-3" /></Link>
            </Button>
          )}
        </Card>
      ))}
    </div>
  </div>
);
